package br.gov.go.pge.permanenciaweb.site.components

import br.gov.go.pge.permanenciaweb.shared.EventDto
import br.gov.go.pge.permanenciaweb.shared.OccupantDto
import br.gov.go.pge.permanenciaweb.shared.SemSaidaRowDto

/*
 * Pure filtering helpers shared by both tabs.
 *
 * Filters are per column and stored in one map for the whole dashboard. Columns that exist in
 * both tables share the same key (`ANDAR`, `UNIDADE`, `SALA`, `TIPO`, `PESSOA`), so a filter set
 * on the roster also narrows the events tab — the "sync slicers" behaviour. Columns that exist in
 * only one table get their own key.
 *
 * Text matching is a plain substring match (SQL `LIKE '%param%'`), case- and accent-insensitive.
 * It intersects with the column's checkbox selection and with every other column.
 */

/**
 * Filter identity across the dashboard.
 *
 * [PESSOA] is the roster's "Nome" and the events' "Pessoa" (the same person); [TIPO] is
 * `tipoAcesso`, which is literally the same field in both DTOs.
 */
internal enum class FilterKey {
    // Shared between roster and events (sync slicers).
    PESSOA,
    ANDAR,
    UNIDADE,
    SALA,
    TIPO,

    // Roster only.
    TELEFONE,
    CARGO,
    ENTRADA,
    ALERTA,

    // Events only.
    HORARIO,
    SENTIDO,
    SETOR,
    FONTE,
    DISPOSITIVO,

    // Sem saída only.
    DIAS_SEM_SAIDA,
    ULTIMO_DIA,
}

/**
 * Which values of a column pass the checkbox side of the filter.
 *
 * [All] is the untouched state (nothing selected, everything passes). [AllExcept] is "selecionar
 * tudo" with exclusions — it is exclusion-based on purpose, so values that appear later still pass.
 * [Only] is the classic "check the ones I want" mode; blank values never match it.
 */
internal sealed interface FilterSelection {
    data object All : FilterSelection
    data class AllExcept(val excluded: Set<String>) : FilterSelection
    data class Only(val included: Set<String>) : FilterSelection

    /** Values the popover must keep visible even when the search hides them. */
    val values: Set<String>
        get() = when (this) {
            All -> emptySet()
            is AllExcept -> excluded
            is Only -> included
        }

    /** Each unchecked (AllExcept) or checked (Only) option counts as one; [All] counts zero. */
    val activeCount: Int
        get() = when (this) {
            All -> 0
            is AllExcept -> excluded.size
            is Only -> included.size
        }

    /**
     * Predicado de **filtragem**: [All] deixa tudo passar (não restringe nada), [AllExcept] barra as
     * exclusões e [Only] só deixa passar o que foi marcado.
     *
     * Não use isto para desenhar as caixas: em [All] nada está marcado, mas tudo passa — para a UI
     * existe [isChecked].
     */
    fun contains(value: String): Boolean = when (this) {
        All -> true
        is AllExcept -> value !in excluded
        is Only -> value in included
    }

    /**
     * Predicado de **exibição**: em [All] nenhuma caixa aparece marcada (coluna intocada), mesmo
     * que a filtragem deixe tudo passar. [AllExcept] e [Only] desenham exatamente o que filtram.
     */
    fun isChecked(value: String): Boolean = when (this) {
        All -> false
        is AllExcept -> value !in excluded
        is Only -> value in included
    }
}

/** Visual state of the "Selecionar tudo" checkbox. */
internal enum class SelectAllState { CHECKED, INDETERMINATE, UNCHECKED }

internal val FilterSelection.selectAllState: SelectAllState
    get() = when (this) {
        FilterSelection.All -> SelectAllState.UNCHECKED
        is FilterSelection.AllExcept -> if (excluded.isEmpty()) SelectAllState.CHECKED else SelectAllState.INDETERMINATE
        is FilterSelection.Only -> SelectAllState.INDETERMINATE
    }

/**
 * "Selecionar tudo" cycle: unchecked → everything selected → unchecked again.
 *
 * Em quadrado (parcial) o clique marca tudo — é o que torna "marcar tudo, depois desmarcar as
 * exceções" um caminho de dois passos. Já em marcado, o clique desmarca tudo, o que equivale a
 * limpar a seleção da coluna (o texto digitado permanece).
 */
internal fun FilterSelection.toggledSelectAll(): FilterSelection = when (selectAllState) {
    SelectAllState.UNCHECKED, SelectAllState.INDETERMINATE -> FilterSelection.AllExcept(emptySet())
    SelectAllState.CHECKED -> FilterSelection.All
}

/** Checks/unchecks one value, keeping the current mode. */
internal fun FilterSelection.toggled(value: String): FilterSelection = when (this) {
    FilterSelection.All -> FilterSelection.Only(setOf(value))
    is FilterSelection.AllExcept -> FilterSelection.AllExcept(excluded.toggled(value))
    is FilterSelection.Only -> included.toggled(value).let { next ->
        when {
            // Uma seleção vazia é "sem filtro", não "nada passa".
            next.isEmpty() -> FilterSelection.All
            else -> FilterSelection.Only(next)
        }
    }
}

/** Active filters of one column: a text query and/or a selection mode. */
internal data class ColumnFilter(
    val text: String = "",
    val selection: FilterSelection = FilterSelection.All,
) {
    /** Trimmed text; whitespace only is not a filter. */
    val query: String get() = text.trim()

    /** The text input counts as one filter; each checked/unchecked option counts as one. */
    val activeCount: Int get() = (if (query.isEmpty()) 0 else 1) + selection.activeCount

    val isActive: Boolean get() = activeCount > 0

    /** Kept in the state even when it does not narrow anything, so the "tudo" mode survives. */
    val isStored: Boolean get() = selection != FilterSelection.All || query.isNotEmpty()

    companion object {
        val Empty = ColumnFilter()
    }
}

/** Columns whose values are unique per row (timestamps, phone numbers): text input only. */
internal val TEXT_ONLY_FILTER_KEYS: Set<FilterKey> =
    setOf(
        FilterKey.TELEFONE,
        FilterKey.ENTRADA,
        FilterKey.HORARIO,
        FilterKey.DIAS_SEM_SAIDA,
        FilterKey.ULTIMO_DIA,
    )


internal val ROSTER_FILTER_KEYS: Set<FilterKey> = RosterSortField.entries.map { it.filterKey }.toSet()
internal val EVENTS_FILTER_KEYS: Set<FilterKey> = EventSortField.entries.map { it.filterKey }.toSet()
internal val SEM_SAIDA_FILTER_KEYS: Set<FilterKey> = SemSaidaSortField.entries.map { it.filterKey }.toSet()

internal val RosterSortField.filterKey: FilterKey
    get() = when (this) {
        RosterSortField.NOME -> FilterKey.PESSOA
        RosterSortField.TIPO -> FilterKey.TIPO
        RosterSortField.TELEFONE -> FilterKey.TELEFONE
        RosterSortField.SALA -> FilterKey.SALA
        RosterSortField.ANDAR -> FilterKey.ANDAR
        RosterSortField.UNIDADE -> FilterKey.UNIDADE
        RosterSortField.CARGO -> FilterKey.CARGO
        RosterSortField.ENTRADA -> FilterKey.ENTRADA
        RosterSortField.ALERTA -> FilterKey.ALERTA
    }

internal val EventSortField.filterKey: FilterKey
    get() = when (this) {
        EventSortField.HORARIO -> FilterKey.HORARIO
        EventSortField.SENTIDO -> FilterKey.SENTIDO
        EventSortField.PESSOA -> FilterKey.PESSOA
        EventSortField.TIPO -> FilterKey.TIPO
        EventSortField.SETOR -> FilterKey.SETOR
        EventSortField.ANDAR -> FilterKey.ANDAR
        EventSortField.SALA -> FilterKey.SALA
        EventSortField.UNIDADE -> FilterKey.UNIDADE
        EventSortField.FONTE -> FilterKey.FONTE
        EventSortField.DISPOSITIVO -> FilterKey.DISPOSITIVO
    }

internal val SemSaidaSortField.filterKey: FilterKey
    get() = when (this) {
        SemSaidaSortField.NOME -> FilterKey.PESSOA
        SemSaidaSortField.TIPO -> FilterKey.TIPO
        SemSaidaSortField.CARGO -> FilterKey.CARGO
        SemSaidaSortField.SETOR -> FilterKey.SETOR
        SemSaidaSortField.SALA -> FilterKey.SALA
        SemSaidaSortField.ANDAR -> FilterKey.ANDAR
        SemSaidaSortField.UNIDADE -> FilterKey.UNIDADE
        SemSaidaSortField.DIAS -> FilterKey.DIAS_SEM_SAIDA
        SemSaidaSortField.ULTIMO_DIA -> FilterKey.ULTIMO_DIA
    }

/** Value of [key] in a roster row, or `null` when the key does not apply to that table. */
internal fun FilterKey.rosterText(o: OccupantDto): String? = when (this) {
    FilterKey.PESSOA -> o.nome
    FilterKey.TIPO -> o.tipoAcesso
    FilterKey.TELEFONE -> o.telefone
    FilterKey.SALA -> o.sala
    FilterKey.ANDAR -> o.andar
    FilterKey.UNIDADE -> o.unidade
    FilterKey.CARGO -> o.cargo
    FilterKey.ENTRADA -> o.entrada
    FilterKey.ALERTA -> o.alertasTexto()
    FilterKey.HORARIO, FilterKey.SENTIDO, FilterKey.SETOR, FilterKey.FONTE, FilterKey.DISPOSITIVO,
    FilterKey.DIAS_SEM_SAIDA, FilterKey.ULTIMO_DIA -> null
}

/** Value of [key] in an event row, or `null` when the key does not apply to that table. */
internal fun FilterKey.eventText(e: EventDto): String? = when (this) {
    FilterKey.HORARIO -> e.horario
    FilterKey.SENTIDO -> e.sentido
    FilterKey.PESSOA -> e.pessoa
    FilterKey.TIPO -> e.tipoAcesso
    FilterKey.SETOR -> e.setor
    FilterKey.ANDAR -> e.andar
    FilterKey.SALA -> e.sala
    FilterKey.UNIDADE -> e.unidade
    FilterKey.FONTE -> e.fonteNome
    FilterKey.DISPOSITIVO -> e.dispositivo
    FilterKey.TELEFONE, FilterKey.CARGO, FilterKey.ENTRADA, FilterKey.ALERTA,
    FilterKey.DIAS_SEM_SAIDA, FilterKey.ULTIMO_DIA -> null
}

/** Value of [key] in a Sem saída report row, or `null` when the key does not apply. */
internal fun FilterKey.semSaidaText(r: SemSaidaRowDto): String? = when (this) {
    FilterKey.PESSOA -> r.nome
    FilterKey.TIPO -> r.tipoAcesso
    FilterKey.CARGO -> r.cargo
    FilterKey.SETOR -> r.setor
    FilterKey.SALA -> r.sala
    FilterKey.ANDAR -> r.andar
    FilterKey.UNIDADE -> r.unidade
    FilterKey.DIAS_SEM_SAIDA -> r.diasSemSaida.toString()
    FilterKey.ULTIMO_DIA -> r.ultimoDia
    FilterKey.TELEFONE, FilterKey.ENTRADA, FilterKey.ALERTA,
    FilterKey.HORARIO, FilterKey.SENTIDO, FilterKey.FONTE, FilterKey.DISPOSITIVO -> null
}

// ------------------------------------------------------------------ matching

/**
 * The folding table itself lives in `:shared` (`normalizeForSearch`) so the backend's directory
 * search and the client-side column filters can never drift. These wrappers keep the existing
 * call sites and tests on the dashboard's vocabulary.
 */
internal fun normalizeForSearch(value: String?): String =
    br.gov.go.pge.permanenciaweb.shared.normalizeForSearch(value)

/** Case- and accent-insensitive substring match (`LIKE '%query%'`). A blank query matches all. */
internal fun matchesContains(value: String?, query: String): Boolean =
    br.gov.go.pge.permanenciaweb.shared.matchesContains(value, query)

/**
 * Text and checkboxes intersect inside a column.
 *
 * [tokens] are the values the checkbox selection matches against. Most columns have exactly one
 * per row; "Alerta" has one per alert code, so a row carrying "Inconsistente" *and* "Liberado"
 * passes when either one is checked.
 */
private fun ColumnFilter.matches(tokens: List<String>, text: String?): Boolean {
    val values = tokens.map { it.trim() }.ifEmpty { listOf("") }
    val selectionOk = when (val current = selection) {
        FilterSelection.All -> true
        is FilterSelection.AllExcept -> values.none { it in current.excluded }
        is FilterSelection.Only -> values.any { it in current.included }
    }
    return selectionOk && matchesContains(text, query)
}

/**
 * Narrows [this] with [filters]: AND across columns, and inside each column the text intersects
 * with the checked options. An empty map is the identity.
 *
 * [values] exists for multi-valued cells (the alert column); by default a cell has one value.
 */
internal fun <T> List<T>.applyColumnFilters(
    filters: Map<FilterKey, ColumnFilter>,
    values: ((T, FilterKey) -> List<String>)? = null,
    text: (T, FilterKey) -> String?,
): List<T> {
    if (filters.isEmpty()) return this
    return filter { row ->
        filters.all { (key, filter) ->
            filter.matches(values?.invoke(row, key) ?: listOfNotNull(text(row, key)), text(row, key))
        }
    }
}

// -------------------------------------------------------------------- facets

/** All distinct non-blank values of [values], sorted (code-unit order, as before). */
internal fun facetOptions(values: List<String?>): List<String> =
    values.mapNotNull { it?.trim()?.takeIf { v -> v.isNotEmpty() } }.distinct().sorted()

/** Option list per column, derived from the union of roster and event values. */
internal fun facetOptions(
    occupants: List<OccupantDto>,
    events: List<EventDto>,
): Map<FilterKey, List<String>> =
    (ROSTER_FILTER_KEYS + EVENTS_FILTER_KEYS)
        .filterNot { it in TEXT_ONLY_FILTER_KEYS }
        .associateWith { key ->
            if (key == FilterKey.ALERTA) {
                // Fixed vocabulary: the facet must not vanish when nobody carries an alert today.
                listOf(ALERTA_INCONSISTENTE, ALERTA_LIBERADO, ALERTA_EXPIRADO, ALERTA_SEM_SAIDA)
            } else {
                facetOptions(occupants.map { key.rosterText(it) } + events.map { key.eventText(it) })
            }
        }

/** Option list for the Sem saída table, derived from its own rows. */
internal fun semSaidaFacetOptions(rows: List<SemSaidaRowDto>): Map<FilterKey, List<String>> =
    SEM_SAIDA_FILTER_KEYS
        .filterNot { it in TEXT_ONLY_FILTER_KEYS }
        .associateWith { key -> facetOptions(rows.map { key.semSaidaText(it) }) }

/** Only the filters that apply to a table; the other table's columns must not exclude rows. */
internal fun Map<FilterKey, ColumnFilter>.forTable(keys: Set<FilterKey>): Map<FilterKey, ColumnFilter> =
    if (keys.containsAll(this.keys)) this else filterKeys { it in keys }

/**
 * Which filters a table owns: its column keys and, for the roster, the KPI card quick filters.
 * "Limpar tudo" clears exactly this scope, so clearing the events does not drop the roster cards.
 */
internal data class FilterScope(val keys: Set<FilterKey>, val includesQuickFilters: Boolean = false)

internal val ROSTER_SCOPE = FilterScope(ROSTER_FILTER_KEYS, includesQuickFilters = true)
internal val EVENTS_SCOPE = FilterScope(EVENTS_FILTER_KEYS)
internal val SEM_SAIDA_SCOPE = FilterScope(SEM_SAIDA_FILTER_KEYS)

/** True when any filter of [keys] is set — used for the "Limpar tudo" enabled state. */
internal fun Map<FilterKey, ColumnFilter>.hasAny(keys: Set<FilterKey>): Boolean =
    keys.any { it in this }

internal fun Set<String>.toggled(value: String): Set<String> =
    if (value in this) this - value else this + value
